<template>
  <Page subpage title="Equipment">
    <template #actions>
      <ActionsMenu
        :actions="actions"
        placement="bottom"
        @add="toggleModal"
        @convert="toggleConvertModal"
      >
        <template #trigger>
          <sera-button
            label="Equipment Actions"
            icon="chevron-down"
            iconRight
            data-cy="appointment-actions-trigger"
          />
        </template>
      </ActionsMenu>
    </template>
    <div class="d-flex align-items-end justify-content-between mb-4 flex-wrap">
      <div class="d-flex align-items-center flex-wrap">
        <ToggleSwitch
          v-model="activeOnly"
          label-on="Active"
          label-off="All"
          class="mr-3 mb-xs-2"
        />
        <CustomerAddressSelect
          v-if="!innerAddressFlow && currentUser"
          v-model="selectedAddressId"
          label=""
          class="customer-address-select mr-3 mb-xs-2"
          :customer="currentUser"
          placeholder="All Addresses"
          data-cy="equipment-address-select"
          @selected-address="handleSelectCustomerAddress"
        />
        <TextField
          v-model="search"
          class="mr-3 mb-0"
          label="Search"
          placeholder="Search Name"
          search
          no-label
        />
      </div>
    </div>
    <div v-if="loading" class="loading">
      <Spinner class="mt-3" message="Loading Equipment..." />
    </div>
    <div v-else>
      <CustomerEquipmentList
        v-if="equipmentData"
        :equipment-data="equipmentData"
        @edit-equipment="toggleModal"
      />
      <PaginationControls
        v-if="equipmentPager"
        :pager="equipmentPager"
        @update:data="updateData"
      />
    </div>
    <EquipmentModal
      v-if="modalOpen"
      :always-show-address-select="!address"
      :customer-id="getProperty(currentUser, 'id')"
      :address="selectedAddress || address"
      :equipment-id="getProperty(selectedEquipment, 'id')"
      @hide-modal="hideEquipmentModal"
      @save-success="fetchData"
    />
    <ConvertGenericEquipmentModal
      v-if="convertModal"
      @hide-modal="toggleConvertModal"
    ></ConvertGenericEquipmentModal>
  </Page>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { computed, onMounted, ref, watch } from "vue";
import { getProperty } from "@/lib/helpers.js";
import Pagination from "@/lib/pagination";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import PaginationControls from "@/components/PaginationControls.vue";
import PaginationLimit from "@/components/PaginationLimit.vue";
import Spinner from "@/components/Spinner2.vue";
import TextField from "@/components/TextField.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import ConvertGenericEquipmentModal from "@/Equipment/ConvertGenericEquipmentModal.vue";
import EquipmentModal from "@/Equipment/EquipmentModal.vue";
import { fullAddress } from "@/mixins";
import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";
import { Equipment } from "@/typings/equipment";
import CustomerAddressSelect from "../AddressSelector/CustomerAddressSelect.vue";
import CustomerEquipmentList from "./CustomerEquipmentList.vue";

const props = defineProps({
  innerAddressFlow: {
    type: Boolean,
    default: false
  },
  address: {
    type: Object as () => Address,
    default: null
  }
});

const store = useStore();

const loading = ref(true);
const equipmentPager = ref<Pagination | null>(null);
const equipmentData = ref<Equipment[]>([]);
const activeOnly = ref(true);
const modalOpen = ref(false);
const convertModal = ref(false);
const selectedEquipment = ref<Equipment | null>(null);
const totalEquipment = ref(0);
const search = ref("");
const selectedAddressId = ref();
const selectedAddress = ref<Address>();

onMounted(() => {
  // Setup pager with default filters
  equipmentPager.value = new Pagination({
    baseURL: `${window.location.origin}/admin/v4/equipment`,
    perPage: 10,
    filteredBy: [{ key: "archived", value: !activeOnly.value }]
  });
  if (props.address) {
    selectedAddressId.value = props.address.id;
    equipmentPager.value?.filter({
      key: "address_id",
      value: props.address.id
    });
  }
  if (currentUser.value) {
    equipmentPager.value.filter({
      key: "customer_id",
      value: currentUser.value.id
    });
    fetchData();
    if (!addressOptions.value.length) {
      store.dispatch("fetchCustomerAddresses", {
        customerId: currentUser.value.id
      });
    }
  }
});

const currentUser = computed<Customer | null>(() => store.state.currentUser);

const addressOptions = computed<{ label: string; value: any }[]>(() => {
  const addresses: Address[] = store.state.currentUserAddresses;
  return addresses.map((address) => ({
    label: fullAddress(address),
    value: address.id
  }));
});

const actions = computed(() => {
  return [
    {
      label: "Add Equipment",
      eventName: "add",
      icon: "fa-solid fa-plus"
    },
    {
      label: "Generic Equipment Conversion",
      eventName: "convert",
      icon: "fa-solid fa-wand-magic-sparkles"
    }
  ];
});

function handleSelectCustomerAddress(address: Address) {
  selectedAddress.value = address;
}

const fetchData = async () => {
  loading.value = true;
  try {
    const response = await equipmentPager.value?.getData();
    if (response) {
      equipmentData.value = response.data.data;
      totalEquipment.value = response.data.meta?.pagination?.total_objects || 0;
    }
  } catch (e) {
    // Do nothing
  }
  loading.value = false;
};
const toggleModal = (equipment = null) => {
  selectedEquipment.value = equipment;
  modalOpen.value = !modalOpen.value;
};
const toggleConvertModal = () => {
  convertModal.value = !convertModal.value;
};
const updateData = (val: Equipment[]) => {
  equipmentData.value = val;
};

const handleSearch = debounce((value: string) => {
  equipmentPager.value?.filter({
    key: "search",
    value: value?.toString() || null
  });
  fetchData();
}, 250);

const hideEquipmentModal = () => {
  // Clear selected equipment when modal closes
  selectedEquipment.value = null;
  toggleModal();
};

watch(activeOnly, (value, oldValue) => {
  if (value !== oldValue) {
    equipmentPager.value?.filter({
      key: "archived",
      value: value ? "false" : null
    });
    fetchData();
  }
});

watch(selectedAddressId, (value, oldValue) => {
  if (value !== oldValue) {
    equipmentPager.value?.filter({
      key: "address_id",
      value: value || null
    });
    fetchData();
  }
});

watch(
  () => currentUser.value?.id,
  (value, oldValue) => {
    if (value !== oldValue && value) {
      equipmentPager.value?.filter({
        key: "customer_id",
        value: value?.toString() || null
      });
      fetchData();
      store.dispatch("fetchCustomerAddresses", {
        customerId: value
      });
    }
  }
);

watch(search, (value, oldValue) => {
  if (value !== oldValue) {
    handleSearch(value);
  }
});

watch(search, (value, oldValue) => {
  if (value !== oldValue) {
    handleSearch(value);
  }
});
</script>

<style lang="scss" scoped>
.customer-address-select {
  width: 300px;
}
.loading {
  min-height: 150px;
}
</style>
